import { Link } from "react-router-dom";
import { 
  Play,
  Sparkles,
  Droplets,
  ArrowRight,
  ChevronRight,
  Building2,
  Shield,
  Zap,
  FileText,
  Award,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Brand Assets
const STERILE_MATE_LOGO = "/logos/askisaac-b-monogram.svg";

const DEMOS = [
  {
    id: "how-it-works",
    title: "How AskIsaac Works",
    subtitle: "The Complete Backorder Solution",
    description: "See how we resolve hospital backorders in hours, not weeks. Our intelligent platform connects you with 50+ verified suppliers nationwide.",
    path: "/how-it-works",
    icon: Play,
    color: "cyan",
    gradient: "from-cyan-900/40 to-slate-900/60",
    border: "border-cyan-500/40",
    highlights: [
      "6-step process walkthrough",
      "Real-time supplier bidding",
      "24-48hr standard response"
    ],
    featured: true
  },
  {
    id: "evs",
    title: "Janitorial & EVS Solutions",
    subtitle: "Environmental Services Excellence",
    description: "Hospital-grade cleaning supplies, disinfectants, and environmental services equipment. From floor care to waste management.",
    path: "/demo/evs",
    icon: Sparkles,
    color: "emerald",
    gradient: "from-emerald-900/40 to-slate-900/60",
    border: "border-emerald-500/40",
    highlights: [
      "Complete EVS product catalog",
      "Hospital-grade quality",
      "Fast fulfillment network"
    ],
    featured: true
  },
  {
    id: "water",
    title: "Water Purification Systems",
    subtitle: "AAMI ST108 Compliant Solutions",
    description: "RO/DI systems, water testing, and compliance solutions. Healthcare water purification experts for sterile processing.",
    path: "/demo/water-purification",
    icon: Droplets,
    color: "blue",
    gradient: "from-blue-900/40 to-slate-900/60",
    border: "border-blue-500/40",
    highlights: [
      "AAMI ST108 compliance",
      "Water purification solutions",
      "ISO 17025 testing"
    ],
    featured: true
  }
];

const ADDITIONAL_CAPABILITIES = [
  {
    title: "Medical Supplies",
    description: "730+ products across surgical, diagnostic, and patient care categories",
    icon: FileText,
    path: "/hospital/marketplace"
  },
  {
    title: "Sterile Processing",
    description: "SPD equipment, autoclaves, and sterilization supplies",
    icon: Shield,
    path: "/hospital/capabilities"
  },
  {
    title: "Compliance Solutions",
    description: "Documentation, training, and regulatory compliance support",
    icon: Award,
    path: "/aami-st108"
  }
];

export default function DemoGalleryPage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-slate-900 via-cyan-900/30 to-slate-900 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="text-center">
            <Link to="/" className="inline-block mb-6">
              <img src={STERILE_MATE_LOGO} alt="AskIsaac" className="w-32 md:w-40 h-auto mx-auto" />
            </Link>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Explore Our Capabilities
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
              Discover how Sterile Mate transforms healthcare supply chain management. 
              Watch our demos to see how we solve your biggest challenges.
            </p>
          </div>
        </div>
      </div>

      {/* Main Demo Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {DEMOS.map((demo) => {
            const Icon = demo.icon;
            return (
              <Link
                key={demo.id}
                to={demo.path}
                className={`group relative bg-gradient-to-br ${demo.gradient} border ${demo.border} rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-${demo.color}-500/20 transition-all duration-300 hover:scale-[1.02]`}
                data-testid={`demo-card-${demo.id}`}
              >
                {demo.featured && (
                  <div className={`absolute top-4 right-4 px-3 py-1 bg-${demo.color}-500/20 border border-${demo.color}-500/30 rounded-full`}>
                    <span className={`text-xs font-semibold text-${demo.color}-400`}>Featured</span>
                  </div>
                )}
                
                <div className="p-6 md:p-8">
                  <div className={`w-14 h-14 rounded-xl bg-${demo.color}-500/20 border border-${demo.color}-500/30 flex items-center justify-center mb-5`}>
                    <Icon className={`w-7 h-7 text-${demo.color}-400`} />
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{demo.title}</h3>
                  <p className={`text-${demo.color}-400 font-medium text-sm mb-4`}>{demo.subtitle}</p>
                  <p className="text-slate-300 text-sm mb-6">{demo.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {demo.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-slate-400 text-sm">
                        <CheckCircle2 className={`w-4 h-4 text-${demo.color}-500 flex-shrink-0`} />
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className={`flex items-center gap-2 text-${demo.color}-400 font-semibold group-hover:gap-3 transition-all`}>
                    <Play className="w-5 h-5" />
                    <span>Watch Demo</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Additional Capabilities */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-8 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">More Capabilities</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {ADDITIONAL_CAPABILITIES.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <Link
                  key={idx}
                  to={cap.path}
                  className="group bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/30 hover:border-slate-500/50 rounded-xl p-5 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1 group-hover:text-cyan-400 transition-colors">{cap.title}</h4>
                      <p className="text-slate-400 text-sm">{cap.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-cyan-900/30 via-slate-800/50 to-slate-900/50 rounded-2xl border border-cyan-500/30 p-8 md:p-12 text-center">
          <Building2 className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Transform Your Supply Chain?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of healthcare facilities who trust Sterile Mate for their critical supply needs. 
            Get started today — no contracts, no commitments.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/hospital/login">
              <Button className="bg-cyan-600 hover:bg-cyan-500 text-white px-8 py-3 text-lg">
                Hospital Portal
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-500 text-slate-300 hover:bg-slate-700 px-8 py-3 text-lg">
                Back to Home
              </Button>
            </Link>
          </div>
          <p className="text-slate-500 text-sm mt-6">
            Questions? Call <strong className="text-slate-400">800.988.3892</strong> or email <strong className="text-slate-400">support@sterilemate.com</strong>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 Sterile Mate LLC | AskIsaac Healthcare Supply Platform
          </p>
        </div>
      </footer>
    </div>
  );
}
